import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Loader2, ArrowRight, ArrowLeft, Check, Plus, Sparkles, X, RefreshCw, Globe } from 'lucide-react';
import { Button } from './ui/button';
import './ShowFinderWizard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_SHOWS = 10;

const REGION_OPTIONS = [
  { value: 'NZ', label: 'New Zealand' },
  { value: 'AU', label: 'Australia' },
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'global', label: 'Other / Global' },
];

const ShowFinderWizard = ({ open, onClose, onCompleted, user, onUserUpdated }) => {
  // Step 0 = profile (username + region), shown only until user has saved a region
  const needsProfileStep = !!user && !user.region;
  const [step, setStep] = useState(needsProfileStep ? 0 : 1);

  // Profile step state
  const [usernameInput, setUsernameInput] = useState(user?.username || '');
  const [regionInput, setRegionInput] = useState(user?.region || 'NZ');
  const [savingProfile, setSavingProfile] = useState(false);

  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [shows, setShows] = useState([]);
  const [showsPage, setShowsPage] = useState(1);
  const [hasMoreShows, setHasMoreShows] = useState(true);
  const [selectedShows, setSelectedShows] = useState([]);
  const [loadingGenres, setLoadingGenres] = useState(false);
  const [loadingShows, setLoadingShows] = useState(false);
  const [streamingServices, setStreamingServices] = useState([]);
  const [showDetails, setShowDetails] = useState({}); // tmdb_id → { status, streaming_service }
  const [currentDetailIdx, setCurrentDetailIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  const showGridRef = useRef(null);

  const fetchStreamingServices = async (region) => {
    try {
      const url = region
        ? `${API}/streaming-services?region=${encodeURIComponent(region)}`
        : `${API}/streaming-services`;
      const sRes = await axios.get(url, { withCredentials: true });
      setStreamingServices(sRes.data || []);
    } catch (_) {
      // Non-fatal; the per-show streaming dropdown is optional
    }
  };

  // Load curated genres + streaming services on open
  useEffect(() => {
    if (!open) return;

    // Reset state
    setSelectedGenres([]);
    setSelectedShows([]);
    setShows([]);
    setShowDetails({});
    setCurrentDetailIdx(0);

    // Decide starting step based on whether user already has a region saved
    const startStep = !!user && !user.region ? 0 : 1;
    setStep(startStep);
    setUsernameInput(user?.username || '');
    setRegionInput(user?.region || 'NZ');

    setLoadingGenres(true);
    Promise.all([
      axios.get(`${API}/show-finder/genres`, { withCredentials: true }),
      axios.get(
        user?.region
          ? `${API}/streaming-services?region=${encodeURIComponent(user.region)}`
          : `${API}/streaming-services`,
        { withCredentials: true }
      ),
    ])
      .then(([gRes, sRes]) => {
        setGenres(gRes.data.genres || []);
        setStreamingServices(sRes.data || []);
      })
      .catch(() => toast.error('Failed to load genres'))
      .finally(() => setLoadingGenres(false));
  }, [open]);

  const saveProfileAndContinue = async () => {
    const trimmedUsername = (usernameInput || '').trim();
    if (!REGION_OPTIONS.some((r) => r.value === regionInput)) {
      toast.error('Please pick a region');
      return;
    }
    const usernameChanged = !!trimmedUsername && trimmedUsername !== (user?.username || '');
    if (usernameChanged && !/^[a-zA-Z0-9_]{4,20}$/.test(trimmedUsername)) {
      toast.error('Username must be 4–20 letters, numbers or underscores');
      return;
    }

    setSavingProfile(true);
    try {
      // Update username first so we don't persist a region with a broken username state
      if (usernameChanged) {
        await axios.put(
          `${API}/auth/username`,
          { username: trimmedUsername },
          { withCredentials: true }
        );
      }

      // Save region
      await axios.put(
        `${API}/auth/profile`,
        { region: regionInput },
        { withCredentials: true }
      );

      // Refresh streaming services for the selected region so step 3 reflects it
      await fetchStreamingServices(regionInput);

      // Notify parent so it can update the user object in App state
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        onUserUpdated && onUserUpdated(me.data);
      } catch (_) { /* non-fatal */ }

      setStep(1);
    } catch (e) {
      const detailMsg = e.response?.data?.detail;
      const msg = typeof detailMsg === 'string' ? detailMsg : detailMsg?.message;
      toast.error(msg || 'Failed to save your profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleGenre = (id) => {
    setSelectedGenres((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const toggleShow = (show) => {
    setSelectedShows((prev) => {
      const exists = prev.find((s) => s.tmdb_id === show.tmdb_id);
      if (exists) return prev.filter((s) => s.tmdb_id !== show.tmdb_id);
      if (prev.length >= MAX_SHOWS) {
        toast(`You can pick up to ${MAX_SHOWS} shows`, { icon: '🐑' });
        return prev;
      }
      return [...prev, show];
    });
  };

  const goToShows = async () => {
    if (selectedGenres.length === 0) {
      toast.error('Pick at least one genre');
      return;
    }
    setLoadingShows(true);
    try {
      const res = await axios.get(
        `${API}/show-finder/shows?genre_ids=${selectedGenres.join(',')}&limit=24&page=1`,
        { withCredentials: true }
      );
      setShows(res.data.shows || []);
      setShowsPage(1);
      setHasMoreShows(res.data.has_more !== false);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load shows');
    } finally {
      setLoadingShows(false);
    }
  };

  const fetchMoreShows = async () => {
    setLoadingShows(true);
    try {
      const nextPage = showsPage + 1;
      const res = await axios.get(
        `${API}/show-finder/shows?genre_ids=${selectedGenres.join(',')}&limit=24&page=${nextPage}`,
        { withCredentials: true }
      );
      const newShows = res.data.shows || [];
      if (newShows.length === 0) {
        toast('No more suggestions for those genres', { icon: '🐑' });
        setHasMoreShows(false);
      } else {
        setShows(newShows);
        setShowsPage(nextPage);
        setHasMoreShows(res.data.has_more !== false);
        // Scroll the show grid back to the top so the user sees the new batch from the start
        requestAnimationFrame(() => {
          if (showGridRef.current) {
            showGridRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load more shows');
    } finally {
      setLoadingShows(false);
    }
  };

  const goToDetails = () => {
    if (selectedShows.length === 0) {
      toast.error('Pick at least one show');
      return;
    }
    // Initialise details with sensible defaults
    const initial = {};
    selectedShows.forEach((s) => {
      initial[s.tmdb_id] = { status: 'Currently Watching', streaming_service: '' };
    });
    setShowDetails(initial);
    setCurrentDetailIdx(0);
    setStep(3);
  };

  const updateCurrentDetail = (patch) => {
    const tmdbId = selectedShows[currentDetailIdx].tmdb_id;
    setShowDetails((prev) => ({ ...prev, [tmdbId]: { ...prev[tmdbId], ...patch } }));
  };

  const addCurrentShow = async ({ skipDetails = false } = {}) => {
    const show = selectedShows[currentDetailIdx];
    const detail = showDetails[show.tmdb_id] || {};
    setAdding(true);
    try {
      await axios.post(
        `${API}/shows`,
        {
          title: show.title,
          tmdb_id: show.tmdb_id,
          media_type: show.media_type || 'tv',
          image_url: show.poster_url,
          backdrop_url: show.backdrop_url,
          release_date: show.first_air_date,
          genres: show.genres,
          overview: show.overview,
          status: skipDetails ? 'Currently Watching' : (detail.status || 'Currently Watching'),
          streaming_service: skipDetails ? '' : (detail.streaming_service || ''),
        },
        { withCredentials: true }
      );
      toast.success(`Added "${show.title}" to your shows`, { icon: '🐑' });

      // Notify pages (Dashboard, MyShows) so their counts refresh without manual reload
      try { window.dispatchEvent(new CustomEvent('baadtv:shows-changed')); } catch (_) {}

      if (currentDetailIdx + 1 < selectedShows.length) {
        setCurrentDetailIdx(currentDetailIdx + 1);
      } else {
        // All done — mark complete and close
        try {
          await axios.post(`${API}/show-finder/complete`, {}, { withCredentials: true });
        } catch (_) { /* non-fatal */ }
        onCompleted && onCompleted();
        onClose();
      }
    } catch (e) {
      const detailMsg = e.response?.data?.detail;
      const msg = typeof detailMsg === 'string' ? detailMsg : detailMsg?.message;
      if (e.response?.status === 409) {
        toast(`Already in your list — skipping`, { icon: '✓' });
        if (currentDetailIdx + 1 < selectedShows.length) {
          setCurrentDetailIdx(currentDetailIdx + 1);
        } else {
          try {
            await axios.post(`${API}/show-finder/complete`, {}, { withCredentials: true });
          } catch (_) {}
          onCompleted && onCompleted();
          onClose();
        }
      } else {
        toast.error(msg || 'Failed to add show');
      }
    } finally {
      setAdding(false);
    }
  };

  if (!open) return null;

  const totalSteps = needsProfileStep ? 4 : 3;
  const stepLabel = needsProfileStep ? step + 1 : step;

  return (
    <div className="sf-overlay" data-testid="show-finder-overlay" onClick={onClose}>
      <div className="sf-modal" onClick={(e) => e.stopPropagation()} data-testid="show-finder-modal">
        <button className="sf-close" onClick={onClose} aria-label="Close" data-testid="show-finder-close">
          <X size={20} />
        </button>

        <div className="sf-progress" data-testid="sf-progress">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const idx = needsProfileStep ? i : i + 1;
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className={`sf-progress-bar ${stepLabel >= i + 1 ? 'active' : ''}`} />
                )}
                <div className={`sf-progress-step ${stepLabel >= i + 1 ? 'active' : ''}`} data-testid={`sf-progress-step-${idx}`}>
                  {i + 1}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 0 — Profile (username + region) */}
        {step === 0 && (
          <div className="sf-step" data-testid="sf-step-profile">
            <h2 className="sf-title">
              <Globe size={22} /> Quick — let&apos;s confirm a couple of things
            </h2>
            <p className="sf-subtitle">
              We use your region to show streaming services that are available where you are.
            </p>

            <div className="sf-form-row">
              <label className="sf-label" htmlFor="sf-username">Username</label>
              <input
                id="sf-username"
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                className="sf-select"
                placeholder="your_username"
                data-testid="sf-profile-username"
                maxLength={20}
              />
            </div>

            <div className="sf-form-row">
              <label className="sf-label" htmlFor="sf-region">Region</label>
              <select
                id="sf-region"
                value={regionInput}
                onChange={(e) => setRegionInput(e.target.value)}
                className="sf-select"
                data-testid="sf-profile-region"
              >
                {REGION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="sf-actions">
              <Button variant="ghost" onClick={onClose} data-testid="sf-skip-profile">
                Skip for now
              </Button>
              <Button
                onClick={saveProfileAndContinue}
                disabled={savingProfile}
                className="sf-next-btn"
                data-testid="sf-next-profile"
              >
                {savingProfile ? (
                  <><Loader2 size={16} className="spinning" /> Saving…</>
                ) : (
                  <>Next <ArrowRight size={16} /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 1 — Genres */}
        {step === 1 && (
          <div className="sf-step" data-testid="sf-step-genres">
            <h2 className="sf-title">
              <Sparkles size={22} /> My style of shows are…
            </h2>
            <p className="sf-subtitle">Pick at least one — you can pick a few.</p>

            {loadingGenres ? (
              <div className="sf-loading"><Loader2 className="spinning" /> Loading genres…</div>
            ) : (
              <div className="sf-genre-grid">
                {genres.map((g) => (
                  <button
                    key={g.id}
                    className={`sf-genre-tile ${selectedGenres.includes(g.id) ? 'selected' : ''}`}
                    onClick={() => toggleGenre(g.id)}
                    data-testid={`sf-genre-${g.id}`}
                  >
                    <span className="sf-genre-emoji">{g.emoji}</span>
                    <span className="sf-genre-name">{g.name}</span>
                    {selectedGenres.includes(g.id) && (
                      <span className="sf-genre-check"><Check size={14} /></span>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="sf-actions">
              {needsProfileStep ? (
                <Button variant="ghost" onClick={() => setStep(0)} data-testid="sf-back-genres">
                  <ArrowLeft size={16} /> Back
                </Button>
              ) : (
                <Button variant="ghost" onClick={onClose} data-testid="sf-skip">Skip for now</Button>
              )}
              <Button
                onClick={goToShows}
                disabled={selectedGenres.length === 0 || loadingShows}
                className="sf-next-btn"
                data-testid="sf-next-genres"
              >
                {loadingShows ? <><Loader2 size={16} className="spinning" /> Loading…</> : <>Next <ArrowRight size={16} /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Shows */}
        {step === 2 && (
          <div className="sf-step" data-testid="sf-step-shows">
            <h2 className="sf-title">My shows are…</h2>
            <p className="sf-subtitle">
              Pick up to {MAX_SHOWS} ({selectedShows.length}/{MAX_SHOWS} selected)
            </p>

            {loadingShows ? (
              <div className="sf-loading"><Loader2 className="spinning" /> Finding popular shows…</div>
            ) : shows.length === 0 ? (
              <div className="sf-empty">
                <p>No suggestions left in those genres — you might already track them all!</p>
              </div>
            ) : (
              <div className="sf-show-grid" ref={showGridRef}>
                {shows.map((s) => {
                  const sel = selectedShows.find((x) => x.tmdb_id === s.tmdb_id);
                  return (
                    <button
                      key={s.tmdb_id}
                      className={`sf-show-card ${sel ? 'selected' : ''}`}
                      onClick={() => toggleShow(s)}
                      data-testid={`sf-show-${s.tmdb_id}`}
                    >
                      {s.poster_url ? (
                        <img src={s.poster_url} alt={s.title} className="sf-show-poster" />
                      ) : (
                        <div className="sf-show-poster sf-no-poster">No poster</div>
                      )}
                      <div className="sf-show-info">
                        <div className="sf-show-title">{s.title}</div>
                        <div className="sf-show-meta">
                          {s.first_air_date ? s.first_air_date.slice(0, 4) : '—'}
                          {s.vote_average ? ` · ★ ${s.vote_average.toFixed(1)}` : ''}
                        </div>
                      </div>
                      {sel && <span className="sf-show-check"><Check size={14} /></span>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="sf-actions sf-actions-shows">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="sf-back-shows">
                <ArrowLeft size={16} /> Back
              </Button>
              <Button
                variant="outline"
                onClick={fetchMoreShows}
                disabled={loadingShows || !hasMoreShows}
                className="sf-refresh-btn"
                data-testid="sf-refresh-shows"
              >
                {loadingShows ? <Loader2 size={14} className="spinning" /> : <RefreshCw size={14} />}
                <span className="sf-refresh-label">Show more</span>
              </Button>
              <Button
                onClick={goToDetails}
                disabled={selectedShows.length === 0}
                className="sf-next-btn"
                data-testid="sf-next-shows"
              >
                Next <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Per-show details */}
        {step === 3 && selectedShows[currentDetailIdx] && (
          <div className="sf-step" data-testid="sf-step-details">
            <h2 className="sf-title">
              Add details ({currentDetailIdx + 1} of {selectedShows.length})
            </h2>

            <div className="sf-detail-card">
              {selectedShows[currentDetailIdx].backdrop_url ? (
                <div
                  className="sf-detail-backdrop"
                  style={{ backgroundImage: `url(${selectedShows[currentDetailIdx].backdrop_url})` }}
                />
              ) : null}
              <div className="sf-detail-body">
                <div className="sf-detail-poster-wrap">
                  {selectedShows[currentDetailIdx].poster_url && (
                    <img
                      src={selectedShows[currentDetailIdx].poster_url}
                      alt={selectedShows[currentDetailIdx].title}
                      className="sf-detail-poster"
                    />
                  )}
                </div>
                <div className="sf-detail-text">
                  <div className="sf-detail-title">{selectedShows[currentDetailIdx].title}</div>
                  <div className="sf-detail-meta">
                    {selectedShows[currentDetailIdx].first_air_date ? selectedShows[currentDetailIdx].first_air_date.slice(0, 4) : ''}
                    {selectedShows[currentDetailIdx].genres?.length ? ` · ${selectedShows[currentDetailIdx].genres.slice(0, 2).join(', ')}` : ''}
                  </div>
                  {selectedShows[currentDetailIdx].overview && (
                    <p className="sf-detail-overview">{selectedShows[currentDetailIdx].overview}</p>
                  )}

                  <div className="sf-form-row">
                    <label className="sf-label">Status</label>
                    <select
                      value={showDetails[selectedShows[currentDetailIdx].tmdb_id]?.status || 'Currently Watching'}
                      onChange={(e) => updateCurrentDetail({ status: e.target.value })}
                      className="sf-select"
                      data-testid="sf-detail-status"
                    >
                      <option value="Currently Watching">Currently Watching</option>
                      <option value="Want to Watch">Want to Watch</option>
                      <option value="Waiting for Next Season">Waiting for Next Season</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>

                  <div className="sf-form-row">
                    <label className="sf-label">Streaming on</label>
                    <select
                      value={showDetails[selectedShows[currentDetailIdx].tmdb_id]?.streaming_service || ''}
                      onChange={(e) => updateCurrentDetail({ streaming_service: e.target.value })}
                      className="sf-select"
                      data-testid="sf-detail-streaming"
                    >
                      <option value="">— optional —</option>
                      {streamingServices.map((svc) => (
                        <option key={svc.id || svc.name} value={svc.name}>{svc.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sf-detail-actions">
                    <Button
                      variant="ghost"
                      onClick={() => addCurrentShow({ skipDetails: true })}
                      disabled={adding}
                      data-testid="sf-skip-details"
                      className="sf-skip-link"
                    >
                      Skip details
                    </Button>
                    <Button
                      onClick={() => addCurrentShow()}
                      disabled={adding}
                      className="sf-add-btn"
                      data-testid="sf-add-show"
                    >
                      {adding ? (
                        <><Loader2 size={16} className="spinning" /> Adding…</>
                      ) : currentDetailIdx + 1 === selectedShows.length ? (
                        <><Check size={16} /> Add &amp; Finish</>
                      ) : (
                        <><Plus size={16} /> Add &amp; Next</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowFinderWizard;
